import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  XCircleIcon,
  FireIcon,
} from '@heroicons/react/24/outline';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useError } from '../context/ErrorContext';
import { getVenuesByOwner, updateVenue } from '../firebase/venueService';
import {
  getVenueAnalytics,
  aggregateAnalyticsSummary,
} from '../firebase/analyticsService';
import VenueCardPreview from '../components/VenueCardPreview';
import { toPreviewData } from '../data/venuePreview';
import {
  BrandEmptyState,
  BrandSpinner,
  Dropdown,
  PageShell,
  PANEL,
  PANEL_HOVER,
  PanelTitle,
  RING_SETS,
  SegmentedRule,
  brandButton,
} from '../components/ui/Brand';

// Marketing Overview — the reference implementation of the dashboard's
// fixed dark palette. Every other authenticated page follows this page's
// patterns via components/ui/Brand.js.

const ANALYTICS_WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

// Alternating bar ramps, matching the comp. Horizontal bars, so the ramp
// runs left→right along the bar's length. Literal strings so Tailwind
// keeps them.
const BAR_RAMPS = [
  'bg-gradient-to-r from-brand-orange to-brand-pink',
  'bg-gradient-to-r from-brand-green to-brand-blue',
];

// --- Sub-components ---

const MetricCard = ({ title, value, trend, positive }) => (
  <div className={`${PANEL} ${PANEL_HOVER}`}>
    <h3 className="font-mono text-sm text-white/60">{title}</h3>
    <p className="mt-3 font-mono text-4xl font-bold leading-none text-white">
      {value}
    </p>
    <span
      className={`mt-3 flex items-center gap-1.5 font-mono text-xs ${
        positive ? 'text-brand-green' : 'text-brand-pink'
      }`}
    >
      {positive ? (
        <ArrowTrendingUpIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <ArrowTrendingDownIcon
          className="h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      )}
      {trend}
    </span>
  </div>
);

// Horizontal bar chart. The two axis titles sit outside the plot area and
// are aligned to it rather than to the whole card: the category title runs
// up the left edge, the value title is centred under the bar track only.
//
// Geometry: the day labels occupy a fixed `w-14` gutter so the value axis
// can be drawn as one continuous rule at `left-14` — a per-row border
// would break into segments at every row gap. The track is a flex child,
// so a bar's `width: %` is a percentage of the plot area, and the count
// label lives outside the track and never steals width from it.
const SwipeActivityChart = ({ weeklyData, maxSwipes, days }) => (
  <div className={`${PANEL} flex flex-col`}>
    <PanelTitle title="Swipe Activity" subtitle={`Over ${days} days`} />

    {weeklyData.length > 0 ? (
      <div className="mt-8 flex flex-1 gap-3">
        <span className="self-center whitespace-nowrap font-mono text-xs text-white/60 [writing-mode:vertical-rl] rotate-180">
          Day of the Week
        </span>

        <div className="flex flex-1 flex-col">
          <div className="relative flex flex-1 flex-col justify-center gap-3 border-b border-white/30 pb-3">
            <span
              aria-hidden="true"
              className="absolute bottom-0 left-14 top-0 w-px bg-white/30"
            />

            {weeklyData.map((log, index) => {
              const totalSwipes =
                (log.swipedRight || 0) + (log.swipedLeft || 0);
              const widthPct = Math.max((totalSwipes / maxSwipes) * 100, 2);
              const dayLabel = log.date.toLocaleDateString('en-US', {
                weekday: 'short',
              });

              return (
                <div key={log.id || index} className="flex items-center">
                  <span className="w-14 shrink-0 pr-3 text-right font-mono text-xs text-white/60">
                    {dayLabel}
                  </span>
                  {/* pl-px only: bars must originate *on* the value axis. */}
                  <div className="flex flex-1 items-center gap-3 pl-px">
                    <div className="flex-1">
                      <div
                        className={`h-7 rounded-r transition-all duration-500 ${
                          BAR_RAMPS[index % BAR_RAMPS.length]
                        }`}
                        style={{ width: `${widthPct}%` }}
                        title={`${dayLabel}: ${totalSwipes} swipes`}
                      />
                    </div>
                    <span className="w-10 shrink-0 text-right font-mono text-sm font-bold text-white">
                      {totalSwipes}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Padded to the track's own bounds (the w-14 label gutter on the
              left, the w-10 count column plus its gap on the right) so
              "centred" means centred under the bars, not under the row. */}
          <p className="mt-3 pl-14 pr-[3.25rem] text-center font-mono text-xs text-white/60">
            Number of Swipes
          </p>
        </div>
      </div>
    ) : (
      <p className="flex flex-1 items-center justify-center py-16 font-mono text-sm text-white/50">
        Awaiting first swipe data…
      </p>
    )}
  </div>
);

// Completeness checklist driven entirely off the venue doc — a card that
// scores every row gets shown to more people, so this doubles as the
// nudge toward /venue/edit.
const buildChecklist = (venue) => {
  const hours = venue.hours || {};
  const hoursComplete =
    Object.keys(hours).length > 0 &&
    Object.values(hours).every((day) => day && (day.closed || day.open));
  const socials = venue.socialLinks || {};

  return [
    {
      label: '3 HD images uploaded',
      done: (venue.images || []).length >= 3,
    },
    { label: 'Description written', done: Boolean(venue.description) },
    { label: 'Categories selected', done: (venue.categories || []).length > 0 },
    { label: 'Operating hours set', done: hoursComplete },
    { label: 'Contact number added', done: Boolean(venue.phone) },
    {
      label: 'Socials linked',
      done: Boolean(socials.instagram || socials.facebook || socials.tiktok),
    },
    {
      label: 'Offer added',
      done: (venue.offers || []).length > 0,
    },
    { label: 'Venue video added', done: Boolean(venue.video) },
  ];
};

const CardChecklist = ({ venue }) => {
  const items = buildChecklist(venue);
  const doneCount = items.filter((item) => item.done).length;

  return (
    <div className={`${PANEL} flex flex-col`}>
      <PanelTitle
        title="Card Checklist"
        subtitle={`${doneCount} of ${items.length} complete`}
      />

      <ul className="mt-6 flex-1 space-y-3">
        {items.map((item) => (
          <li
            key={item.label}
            className="flex items-start gap-2.5 font-mono text-sm"
          >
            {item.done ? (
              <CheckCircleIcon
                className="mt-px h-5 w-5 shrink-0 text-brand-green"
                aria-hidden="true"
              />
            ) : (
              <XCircleIcon
                className="mt-px h-5 w-5 shrink-0 text-white/35"
                aria-hidden="true"
              />
            )}
            <span className={item.done ? 'text-white/85' : 'text-white/50'}>
              {item.label}
            </span>
          </li>
        ))}
      </ul>

      {doneCount < items.length && (
        <Link
          to={`/venue/edit/${venue.id}`}
          className={brandButton('primary', 'md', 'mt-6')}
        >
          Finish your card
        </Link>
      )}
    </div>
  );
};

// How long a busyness reading stays meaningful. The consumer app applies the
// same window when deciding whether to show the Vibe Check at all — a stale
// "lively" on a venue that closed two nights ago is worse than showing
// nothing, so both sides expire it rather than trusting the last write.
const BUSYNESS_TTL_MS = 4 * 60 * 60 * 1000;

const BUSYNESS_LEVELS = [
  { value: 'quiet', label: 'Quiet' },
  { value: 'lively', label: 'Lively' },
  { value: 'at-capacity', label: 'At Capacity' },
];

const relativeTime = (date, now) => {
  const mins = Math.round((now - date.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
};

// Tonight's crowd level, not a card property — hence a dashboard control
// rather than a wizard field. Pro subscribers see it in the app as "Vibe
// Check"; everyone else sees nothing either way.
const VibeCheck = ({ venue, onSaved }) => {
  const { showError } = useError();
  const [saving, setSaving] = useState(null);
  // Held in state and ticked, rather than read during render: the reading has
  // to expire on its own after 4h, and an owner leaves this dashboard open all
  // night. A render-time Date.now() would also be an impure read that only
  // re-evaluates when something unrelated happens to re-render.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const updatedAt = venue.busynessUpdatedAt?.toDate
    ? venue.busynessUpdatedAt.toDate()
    : venue.busynessUpdatedAt
      ? new Date(venue.busynessUpdatedAt)
      : null;
  const isStale = !updatedAt || now - updatedAt.getTime() > BUSYNESS_TTL_MS;
  const current = isStale ? '' : venue.currentBusyness || '';

  const set = async (value) => {
    setSaving(value || 'clear');
    try {
      await updateVenue(venue.id, {
        currentBusyness: value,
        busynessUpdatedAt: Timestamp.now(),
      });
      await onSaved();
    } catch (error) {
      showError('Could not update your vibe check.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className={PANEL} data-testid="vibe-check">
      <PanelTitle
        title="Vibe Check"
        subtitle="Tell tonight's swipers how busy you are. Expires after 4 hours."
      />
      <div className="mt-5 flex flex-wrap gap-2">
        {BUSYNESS_LEVELS.map((level) => (
          <button
            key={level.value}
            type="button"
            disabled={Boolean(saving)}
            aria-pressed={current === level.value}
            data-testid={`vibe-${level.value}`}
            onClick={() => set(level.value)}
            className={`rounded-full border px-4 py-2 font-mono text-sm transition disabled:opacity-50 ${
              current === level.value
                ? 'border-transparent bg-brand-warm font-bold text-white'
                : 'border-white/20 bg-white/[0.06] text-white/70 hover:border-white/45 hover:text-white'
            }`}
          >
            {level.label}
          </button>
        ))}
        {current && (
          <button
            type="button"
            disabled={Boolean(saving)}
            data-testid="vibe-clear"
            onClick={() => set('')}
            className="rounded-full px-4 py-2 font-mono text-sm text-white/50 transition hover:text-white disabled:opacity-50"
          >
            Clear
          </button>
        )}
      </div>
      <p className="mt-4 font-mono text-xs text-white/50" data-testid="vibe-status">
        {!updatedAt
          ? 'Not set tonight.'
          : isStale
            ? `Expired — last set ${relativeTime(updatedAt, now)}.`
            : `Set ${relativeTime(updatedAt, now)}.`}
      </p>
    </div>
  );
};

const CustomerView = ({ venue }) => (
  <div className={`${PANEL} flex flex-col`}>
    <PanelTitle
      title="Active Customer View"
      subtitle="Exactly how your venue looks when swiping"
    />
    <div className="mt-6 flex justify-center">
      <VenueCardPreview venueData={toPreviewData(venue)} />
    </div>
  </div>
);

// --- Main Dashboard Component ---

function Dashboard() {
  const { currentUser } = useAuth();
  const { showError } = useError();

  const [venues, setVenues] = useState([]);
  const [analyticsLogs, setAnalyticsLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Both dropdowns. `selectedVenueId` stays null until the owner picks,
  // so the first venue is the default without an extra sync effect.
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [days, setDays] = useState(30);

  const fetchDashboardData = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      const userVenues = await getVenuesByOwner(currentUser.uid);
      setVenues(userVenues);

      if (userVenues.length > 0) {
        const target =
          userVenues.find((v) => v.id === selectedVenueId) || userVenues[0];
        const logs = await getVenueAnalytics(target.id, days);
        setAnalyticsLogs(logs);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load your marketing data.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, showError, selectedVenueId, days]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchDashboardData();
  }, [fetchDashboardData]);

  // --- UI States ---

  if (loading) {
    return (
      <div className="flex min-h-screen flex-1 flex-col items-center justify-center gap-4 bg-brand-ink p-12">
        <BrandSpinner />
        <p className="font-mono text-sm text-white/70">Loading dashboard...</p>
      </div>
    );
  }

  if (venues.length === 0) {
    return (
      <PageShell
        rings={RING_SETS.column}
        width="max-w-2xl"
        className="flex flex-col justify-center"
      >
        <BrandEmptyState
          icon={FireIcon}
          title="No Venue Card Created"
          description="Build your Tinder-style custom venue card to start getting discovered by local groups."
          action={
            <Link to="/venue/create" className={brandButton('primary', 'lg')}>
              + Create Venue Card
            </Link>
          }
        />
      </PageShell>
    );
  }

  const activeVenue = venues.find((v) => v.id === selectedVenueId) || venues[0];

  // --- Process LIVE Analytics Data ---
  const summary = aggregateAnalyticsSummary(analyticsLogs);

  // Safe calculations to prevent dividing by zero
  const impressions = summary.impressions || 1;
  const likeRate = ((summary.swipedRight / impressions) * 100).toFixed(1);
  // Denominated on right swipes, not impressions: a group match can only
  // follow a right swipe, so left swipes would only dilute it. Deliberately
  // NOT labelled a conversion rate — swipedRight pools solo and squad swipes
  // in one counter, so a venue with heavy solo traffic reads low through no
  // fault of its own.
  const matchRate = (
    (summary.groupMatches / (summary.swipedRight || 1)) *
    100
  ).toFixed(1);

  const weeklyData = analyticsLogs.slice(-7);
  const maxSwipes = Math.max(
    ...weeklyData.map((d) => (d.swipedRight || 0) + (d.swipedLeft || 0)),
    1
  );

  return (
    <PageShell rings={RING_SETS.overview}>
      {/* Header */}
      <header className="flex flex-col gap-6">
        <h1 className="font-mono text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
          {activeVenue.name}
        </h1>

        {/* The published pill carries the warm ramp itself, so it reads as
            a status *badge* rather than one more outlined chip. */}
        <span
          className={`inline-flex w-fit items-center gap-2.5 rounded-full px-5 py-2 font-mono text-sm font-bold text-white ${
            activeVenue.published ? 'bg-brand-warm' : 'bg-white/10'
          }`}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full ${
              activeVenue.published ? 'bg-brand-green' : 'bg-white/50'
            }`}
          />
          {activeVenue.published ? 'Published' : 'Draft Mode'}
        </span>

        <SegmentedRule variant="warm" />

        <div className="flex flex-wrap gap-4">
          <Dropdown
            label="View Card"
            value={activeVenue.id}
            onChange={(e) => setSelectedVenueId(e.target.value)}
            options={venues.map((v) => ({ value: v.id, label: v.name }))}
          />
          <Dropdown
            label="Filter"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            options={ANALYTICS_WINDOWS}
          />
        </div>
      </header>

      {/* Top-Level KPIs (Live) */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-6">
        <MetricCard
          title="Right Swipes (Likes)"
          value={summary.swipedRight.toLocaleString()}
          trend={`${summary.impressions > 0 ? likeRate : 0}% Conversion`}
          positive={true}
        />
        <MetricCard
          title="Left Swipes (Passes)"
          value={summary.swipedLeft.toLocaleString()}
          trend="Active Discovery"
          positive={false}
        />
        <MetricCard
          title="Profile Expansions"
          value={summary.clickThroughs.toLocaleString()}
          trend="High intent views"
          positive={true}
        />
        <MetricCard
          title="Group Matches"
          value={summary.groupMatches.toLocaleString()}
          trend={`${summary.swipedRight > 0 ? matchRate : 0}% of right swipes`}
          positive={true}
        />
      </div>

      <SegmentedRule variant="warm" />

      <VibeCheck venue={activeVenue} onSaved={fetchDashboardData} />

      {/* Analytics panels */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.6fr_1fr_1fr]">
        <SwipeActivityChart
          weeklyData={weeklyData}
          maxSwipes={maxSwipes}
          days={days}
        />
        <CardChecklist venue={activeVenue} />
        <CustomerView venue={activeVenue} />
      </div>
    </PageShell>
  );
}

export default Dashboard;
